import React, { useRef } from 'react';
import styled from 'styled-components';
import { Icon, Slider } from '@repo/design-system';
import { space } from '@repo/design-tokens';
import { reachReadout, turnReadout } from '@repo/ui/planner';
import { normaliseRotation } from '@repo/front-end-utils';
import type { FloorPlannerCamera } from '@repo/frontend-types';

/** The Reach slider's range (`unified-camera-panel`, R2.5); a handle drag may store more. */
export const REACH_SLIDER_MAX = 30;
const REACH_SLIDER_MIN = 1;

/** Said with the Turn slider while it is off (R2.6, criterion 9). */
export const NO_DIRECTION = 'A 360° camera has no direction';

const degrees = (n: number) => `${n}°`;
/** The announcement's spoken unit: "Reach 12 metres", not "12 m". */
const metres = (n: number) => `${n} ${n === 1 ? 'metre' : 'metres'}`;

export interface CoverageSlidersProps {
  /** The camera's direction, angle and reach as drawn (`effectiveCoverage`). */
  rotation: number;
  fovAngle: number;
  detectionDistance: number;
  /**
   * Write the selected camera. `report` false while dragging or for a key held down, true once when
   * the interaction ends: one drag is one undo step and one draft save (R2.7).
   */
  onWrite: (patch: Partial<FloorPlannerCamera>, report: boolean) => void;
  /** Say the committed value through the step's polite region (criterion 10). */
  onAnnounce: (text: string) => void;
}

/**
 * The camera panel's Turn, Widen and Reach (`unified-camera-panel`, R2): three compact, one-line
 * sliders, each led by its plan handle's glyph. They replace the marked Coverage slider, its
 * "Reaches N m." line and the row of handle chips.
 *
 * - **Turn** wraps: right at 359° goes to 0°, left at 0° goes to 359°, as the plan handle does. The
 *   design-system slider stops at its ends, so the wrap is taken here, before the slider sees the key.
 * - **Widen** is a plain slider: no Focused, Wide or 360° marks (Rob, 2026-10-29). The plan's Widen
 *   handle keeps its snap.
 * - **Reach** runs 1-30 m. A reach stored past 30 (a handle drag can) reads as itself with the thumb
 *   at the end; moving it writes a value in range.
 */
const CoverageSliders: React.FC<CoverageSlidersProps> = ({
  rotation,
  fovAngle,
  detectionDistance,
  onWrite,
  onAnnounce,
}) => {
  const turn = Math.round(normaliseRotation(rotation)) % 360;
  const omni = fovAngle >= 360;
  const reachOver = detectionDistance > REACH_SLIDER_MAX;

  /*
   * A reach stored past 30 m shows its thumb at 30, so a drag that leaves and comes back to 30 ends
   * where it started and the slider commits nothing, though the live writes changed the stored
   * reach. The last live value is kept until a commit, and an interaction ending with one still
   * uncommitted commits it (review). The wrapper's handlers bubble after the slider's own finish.
   */
  const reachUncommitted = useRef<number | null>(null);
  const commitReach = (v: number) => {
    reachUncommitted.current = null;
    onWrite({ detectionDistance: v }, true);
    onAnnounce(`Reach ${metres(v)}`);
  };
  const settleReach = () => {
    const v = reachUncommitted.current;
    if (v != null) commitReach(v);
  };

  const commitTurn = (v: number) => {
    const next = normaliseRotation(v);
    onWrite({ rotation: next }, true);
    onAnnounce(`Turn ${turnReadout(next)}`);
  };

  /**
   * Turn's wrap (R2.3): a fresh press that would leave 0-359 goes round instead, ±1 or ±10 with
   * Shift, as one write and one announcement, the slider's own clamped step skipped. A HELD key stops
   * at the end: the slider keeps its own value for the whole key interaction and commits it on
   * release, so a wrap in the middle of one would be overwritten by that commit (review).
   */
  const wrapTurn = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (omni) return;
    const up = e.key === 'ArrowRight' || e.key === 'ArrowUp';
    const down = e.key === 'ArrowLeft' || e.key === 'ArrowDown';
    if (!up && !down) return;
    const next = turn + (e.shiftKey ? 10 : 1) * (up ? 1 : -1);
    if (next >= 0 && next <= 359) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.repeat) return;
    commitTurn(next);
  };

  return (
    <Rows>
      <div onKeyDownCapture={wrapTurn}>
        <Slider
          label="Turn"
          layout="inline"
          size="sm"
          surface="on-glass"
          icon={<Icon name="rotate" size="xs" />}
          min={0}
          max={359}
          step={1}
          value={turn}
          format={turnReadout}
          disabled={omni}
          description={omni ? NO_DIRECTION : undefined}
          onChange={v => onWrite({ rotation: normaliseRotation(v) }, false)}
          onCommit={commitTurn}
        />
      </div>
      <Slider
        label="Widen"
        layout="inline"
        size="sm"
        surface="on-glass"
        icon={
          <Turned>
            <Icon name="arrows-left-right" size="xs" />
          </Turned>
        }
        min={1}
        max={360}
        step={1}
        value={fovAngle}
        format={degrees}
        onChange={v => onWrite({ fovAngle: v }, false)}
        onCommit={v => {
          onWrite({ fovAngle: v }, true);
          onAnnounce(`Widen ${degrees(v)}`);
        }}
      />
      <div
        onKeyUp={settleReach}
        onBlur={settleReach}
        onPointerUp={settleReach}
        onPointerCancel={settleReach}
        onLostPointerCapture={settleReach}
      >
        <Slider
          label="Reach"
          layout="inline"
          size="sm"
          surface="on-glass"
          icon={<Icon name="arrows-left-right" size="xs" />}
          min={REACH_SLIDER_MIN}
          max={REACH_SLIDER_MAX}
          step={0.5}
          value={Math.min(detectionDistance, REACH_SLIDER_MAX)}
          // The thumb is held at the end; the words say the stored reach until it is moved.
          format={v =>
            reachOver && v === REACH_SLIDER_MAX ? reachReadout(detectionDistance) : reachReadout(v)
          }
          onChange={v => {
            reachUncommitted.current = v;
            onWrite({ detectionDistance: v }, false);
          }}
          onCommit={commitReach}
        />
      </div>
    </Rows>
  );
};

export default CoverageSliders;

/* The three rows, a small gap apart (R2.2: no more than space[8]). */
const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[2]};
`;

/* Widen's glyph turned a quarter, as the hint row drew it: the arc's tangent. */
const Turned = styled.span`
  display: inline-flex;
  transform: rotate(90deg);
`;
